import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { ChevronDown } from "lucide-react";
import { NAV_GROUPS, domainsInGroup, findDomainByPath } from "../routes/domains";
import { cn } from "@/lib/utils";
import { badgeText } from "./navBadges";
import { openOnScriptedClick } from "./menuTrigger";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const entryTarget = (domain) => domain.subNav[0]?.path || domain.basePath;

/** The little count on a section or a group ("2", "9+"); its words are
 * said by the link's own name, so this is for the eye only. */
function Badge({ count, className }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-primary-foreground",
        className,
      )}
    >
      {badgeText(count)}
    </span>
  );
}

Badge.propTypes = { count: PropTypes.number.isRequired, className: PropTypes.string };

/**
 * The header's sections, in their groups (board #104): Work — Tasks,
 * Discussions, Library, Snippets, Drawings; Labs — Study, House, Domains.
 * Account things (Agents, Household, My token) live in the avatar menu.
 *
 * From lg each group is a menu button ("Work ▾"); below lg, inside the
 * hamburger panel, each group is a heading with its sections listed under
 * it, so a phone never opens a menu inside a menu.
 */
function DomainNav({ badges = {} }) {
  const { pathname } = useLocation();
  const active = findDomainByPath(pathname);
  // Controlled, so a scripted click can open a menu (openOnScriptedClick).
  const [openGroup, setOpenGroup] = useState(null);

  const groups = NAV_GROUPS.map((group) => {
    const domains = domainsInGroup(group.key);
    const waiting = domains.reduce((sum, d) => sum + (badges[d.key] > 0 ? badges[d.key] : 0), 0);
    return { ...group, domains, waiting };
  });

  const countWords = (count) => (count > 0 ? ` (${count} for you)` : "");

  return (
    <>
      {/* Desktop: one menu per group. */}
      <ul className="hidden list-none items-center gap-1 p-0 lg:flex">
        {groups.map((group) => {
          const isActive = active?.group === group.key;
          const setOpen = (open) =>
            setOpenGroup((current) => (open ? group.key : current === group.key ? null : current));
          return (
            <li key={group.key}>
              <DropdownMenu modal={false} open={openGroup === group.key} onOpenChange={setOpen}>
                <DropdownMenuTrigger
                  onClick={openOnScriptedClick(setOpen)}
                  className={cn(
                    "relative flex items-center gap-1 rounded-md px-3 py-2 font-semibold whitespace-nowrap transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                    isActive ? "text-primary" : "text-muted-foreground",
                  )}
                  aria-label={`${group.label}${isActive ? `, current section ${active.label}` : ""}${countWords(group.waiting)}`}
                >
                  {/* Where you are, said in the header (no breadcrumb row):
                      "Work · Tasks ▾"; the other group stays "Labs ▾". */}
                  {group.label}
                  {isActive && (
                    <>
                      <span aria-hidden="true"> · </span>
                      {active.label}
                    </>
                  )}
                  <ChevronDown aria-hidden="true" className="size-4" />
                  {group.waiting > 0 && (
                    <Badge
                      count={group.waiting}
                      className="absolute top-0 right-0 h-4 min-w-4 translate-x-1/2 text-[10px] leading-none ring-2 ring-card"
                    />
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-48">
                  {group.domains.map((domain) => {
                    const here = domain.key === active?.key;
                    const count = badges[domain.key];
                    return (
                      <DropdownMenuItem key={domain.key} asChild>
                        <Link
                          to={entryTarget(domain)}
                          aria-current={here ? "page" : undefined}
                          className={cn(
                            "flex min-h-9 items-center justify-between gap-3 no-underline",
                            here && "font-semibold text-primary",
                          )}
                        >
                          <span>
                            {domain.label}
                            {count > 0 && <span className="sr-only"> ({count} for you)</span>}
                          </span>
                          {count > 0 && <Badge count={count} />}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          );
        })}
      </ul>

      {/* Below lg (inside the hamburger panel): headed lists. */}
      <div className="flex flex-col gap-3 lg:hidden">
        {groups.map((group) => (
          <section key={group.key} aria-labelledby={`nav-group-${group.key}`}>
            <h2
              id={`nav-group-${group.key}`}
              className="px-3 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
            >
              {group.label}
            </h2>
            <ul className="flex list-none flex-col gap-1 p-0">
              {group.domains.map((domain) => {
                const here = domain.key === active?.key;
                const count = badges[domain.key];
                return (
                  <li key={domain.key}>
                    <Link
                      to={entryTarget(domain)}
                      aria-current={here ? "page" : undefined}
                      className={cn(
                        "flex min-h-11 items-center rounded-md px-3 py-2 font-semibold no-underline transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                        here ? "text-primary" : "text-foreground",
                      )}
                    >
                      {domain.label}
                      {count > 0 && (
                        <>
                          {" "}
                          <Badge count={count} className="ml-1" />
                          <span className="sr-only"> ({count} for you)</span>
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </>
  );
}

DomainNav.propTypes = {
  // A count per domain key, e.g. {discussions: 2}; 0 or missing = no badge.
  badges: PropTypes.objectOf(PropTypes.number),
};

export default DomainNav;
